import { Box, BoxProps, SxProps, Theme } from '@mui/material';

export type AppCardMediaOverlayPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'center';

export interface AppCardMediaOverlayProps extends Omit<BoxProps, 'sx'> {
  /**
   * Position of the overlay content inside the media area.
   * Parent media container should be `position: relative`.
   */
  overlayPosition?: AppCardMediaOverlayPosition;
  /**
   * Distance from media edges in theme spacing units.
   * Defaults to 1 (8px) to match existing card overlays.
   */
  inset?: number;
  sx?: SxProps<Theme>;
}

/**
 * Reusable positioned overlay container for media slots (pills, chips, badges).
 * Keeps card media overlays consistent and avoids per-card absolute positioning styles.
 */
export function AppCardMediaOverlay({
  children,
  overlayPosition = 'top-right',
  inset = 1,
  sx,
  ...props
}: AppCardMediaOverlayProps) {
  const verticalPositionSx: SxProps<Theme> =
    overlayPosition === 'top-left' ||
    overlayPosition === 'top-center' ||
    overlayPosition === 'top-right'
      ? { top: (theme) => theme.spacing(inset) }
      : overlayPosition === 'bottom-left' ||
          overlayPosition === 'bottom-center' ||
          overlayPosition === 'bottom-right'
        ? { bottom: (theme) => theme.spacing(inset) }
        : { top: '50%', transform: 'translateY(-50%)' };

  const horizontalPositionSx: SxProps<Theme> =
    overlayPosition === 'top-left' || overlayPosition === 'bottom-left'
      ? {
          left: (theme) => theme.spacing(inset),
          justifyContent: 'flex-start',
        }
      : overlayPosition === 'top-right' || overlayPosition === 'bottom-right'
        ? {
            right: (theme) => theme.spacing(inset),
            justifyContent: 'flex-end',
          }
        : {
            left: '50%',
            transform: overlayPosition === 'center' ? 'translate(-50%, -50%)' : 'translateX(-50%)',
            justifyContent: 'center',
          };

  return (
    <Box
      {...props}
      sx={
        [
          {
            position: 'absolute',
            zIndex: 3,
            display: 'flex',
            maxWidth: (theme) => `calc(100% - ${theme.spacing(inset * 2)})`,
            ...verticalPositionSx,
            ...horizontalPositionSx,
          },
          ...(sx !== undefined ? (Array.isArray(sx) ? sx : [sx]) : []),
        ] as SxProps<Theme>
      }
    >
      {children}
    </Box>
  );
}
